import { apiGet, apiPost } from "./fetch";
import { getGlobalHeadersAsObject } from "./global-headers";

/** The collapsed, customer-facing status set — who the ball is with. */
export type SupportTicketStatus = "waiting_on_support" | "waiting_on_user" | "resolved";

export const SUPPORT_STATUS_LABEL: Record<SupportTicketStatus, string> = {
	waiting_on_support: "With support",
	waiting_on_user: "Waiting for you",
	resolved: "Resolved",
};

export const SUPPORT_STATUS_COLOR: Record<
	SupportTicketStatus,
	"info" | "warning" | "success"
> = {
	waiting_on_support: "info",
	waiting_on_user: "warning",
	resolved: "success",
};

export interface SupportAttachment {
	id: number;
	created: string;
	message: number;
	caption: string;
	name: string;
	content_type: string;
}

export interface SupportMessage {
	id: number;
	created: string;
	ticket: number;
	author: number;
	author_name: string;
	is_from_reporter: boolean;
	body: string;
	attachments: SupportAttachment[];
}

export type SupportAiSuggestionState = "pending" | "done";

export interface SupportAiSuggestion {
	id: number;
	created: string;
	updated: string;
	ticket: number;
	model: string;
	state: SupportAiSuggestionState;
	/** What the investigation found — addressed to the operator. */
	analysis: string;
	/** The reply ready to send — addressed to the customer. */
	text: string;
	requested_by: number | null;
}

export interface SupportTicket {
	id: number;
	created: string;
	updated: string;
	title: string;
	status: SupportTicketStatus;
	reporter: number;
	reporter_seen_at: string | null;
	messages: SupportMessage[];
	// Staff serializer only — absent for the reporter.
	staff_seen_at?: string | null;
	workflow_ticket?: number | null;
	ai_suggestions?: SupportAiSuggestion[];
	reporter_name?: string;
	reporter_company?: string;
}

interface PaginatedSupportTickets {
	results: SupportTicket[];
}

export function fetchSupportTickets(): Promise<SupportTicket[]> {
	return (
		apiGet("/api/v2/support-tickets/") as Promise<PaginatedSupportTickets>
	).then((payload) => payload.results);
}

export function fetchSupportTicket(id: number): Promise<SupportTicket> {
	return apiGet(`/api/v2/support-tickets/${id}/`) as Promise<SupportTicket>;
}

/** Open a ticket: the title plus the thread's opening message. */
export function createSupportTicket(
	title: string,
	body: string
): Promise<SupportTicket> {
	return apiPost("/api/v2/support-tickets/", {
		title,
		body,
	}) as Promise<SupportTicket>;
}

export function replySupportTicket(id: number, body: string): Promise<SupportMessage> {
	return apiPost(`/api/v2/support-tickets/${id}/reply/`, {
		body,
	}) as Promise<SupportMessage>;
}

/** Move my side's watermark to now — opening the thread calls this. */
export function markSupportTicketSeen(id: number): Promise<{ status: string }> {
	return apiPost(`/api/v2/support-tickets/${id}/mark_seen/`, {}) as Promise<{
		status: string;
	}>;
}

/** Tickets carrying unseen writing from the other side — the menu badge. */
export function fetchSupportUnreadCount(): Promise<number> {
	return (
		apiGet("/api/v2/support-tickets/unread_count/") as Promise<{ count: number }>
	).then((payload) => payload.count);
}

export function setSupportTicketStatus(
	id: number,
	status: SupportTicketStatus
): Promise<SupportTicket> {
	return apiPost(`/api/v2/support-tickets/${id}/set_status/`, {
		status,
	}) as Promise<SupportTicket>;
}

/** Hand the ticket to development — thread and pictures travel, no retyping. */
export function exportSupportTicketToWorkflow(id: number): Promise<SupportTicket> {
	return apiPost(
		`/api/v2/support-tickets/${id}/export_to_workflow/`,
		{}
	) as Promise<SupportTicket>;
}

/** Staff ask for a fresh AI draft with a chosen model. */
export function requestSupportSuggestion(
	id: number,
	model: string
): Promise<SupportAiSuggestion> {
	return apiPost(`/api/v2/support-tickets/${id}/suggest_reply/`, {
		model,
	}) as Promise<SupportAiSuggestion>;
}

export function uploadSupportAttachment(
	messageId: number,
	file: File,
	caption: string
): Promise<SupportAttachment> {
	const data = new FormData();
	data.append("file", file);
	data.append("message", messageId.toString());
	data.append("name", file.name);
	data.append("caption", caption);
	// no explicit Content-Type: the browser sets the multipart boundary
	return fetch("/api/v2/support-ticket-attachment/", {
		method: "POST",
		headers: { ...getGlobalHeadersAsObject() },
		body: data,
	}).then((res) => {
		if (!res.ok) {
			throw new Error(`HTTP status code: ${res.status}`);
		}
		return res.json() as Promise<SupportAttachment>;
	});
}

/** The authenticated endpoint streaming one picture's bytes. */
export function supportAttachmentUrl(id: number): string {
	return `/api/v2/support-ticket-attachment/${id}/download/`;
}
